import {defineSlotRecipe} from '@chakra-ui/react3'

export const selectSlotRecipe = defineSlotRecipe({
    className: 'leap-select',
    slots: [
        'label',
        'positioner',
        'trigger',
        'indicator',
        'clearTrigger',
        'item',
        'itemText',
        'itemIndicator',
        'itemGroup',
        'itemGroupLabel',
        'list',
        'content',
        'root',
        'control',
        'valueText',
        'indicatorGroup'
    ],
    base: {
        root: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-4',
            width: 'full',
            color: 'text.primary'
        },
        trigger: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: 'full',
            minH: 'var(--select-trigger-height)',
            '--input-height': 'var(--select-trigger-height)',
            px: 'var(--select-trigger-padding-x)',
            borderRadius: 's',
            border: '1px solid',
            borderColor: 'border.subtle',
            boxShadow: 'none',
            userSelect: 'none',
            textAlign: 'start',
            focusVisibleRing: 'outside',
            focusRingColor: 'border.strong',
            focusRingWidth: '1px',
            focusRingStyle: 'solid',
            _placeholderShown: {
                color: 'text.primary'
            },
            _disabled: {
                layerStyle: 'disabled'
            },
            _invalid: {
                borderColor: 'border.error'
            },
            _expanded: {
                borderBottomLeftRadius: '0',
                borderBottomRightRadius: '0',
                borderBottomColor: 'border.subtle',
                borderTopColor: 'border.strong',
                borderLeftColor: 'border.strong',
                borderRightColor: 'border.strong'
            }
        },
        indicatorGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: '1',
            pos: 'absolute',
            insetEnd: '0',
            top: '0',
            bottom: '0',
            px: 'var(--select-trigger-padding-x)',
            pointerEvents: 'none'
        },
        indicator: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s ease',
            _open: {
                transform: 'rotate(180deg)'
            },
            color: {
                base: 'text.tertiary',
                _disabled: 'icons.secondary',
                _invalid: 'text.system.error'
            }
        },
        content: {
            background: 'background.actions.tertiary',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 'dropdown',
            borderBottomLeftRadius: 's',
            borderBottomRightRadius: 's',
            outline: 0,
            maxH: '96',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxShadow: 'none',
            border: '1px solid',
            borderTopColor: 'border.subtle',
            borderBottomColor: 'border.strong',
            borderLeftColor: 'border.strong',
            borderRightColor: 'border.strong',

            _open: {
                animationStyle: 'slide-fade-in',
                animationDuration: 'fast'
            },
            _closed: {
                animationStyle: 'slide-fade-out',
                animationDuration: 'fastest'
            }
        },
        item: {
            position: 'relative',
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 'spacing-8',
            cursor: 'option',
            justifyContent: 'space-between',
            flex: '1',
            textAlign: 'start',
            borderBottom: '1px solid',
            borderBottomColor: 'border.subtle',
            _highlighted: {
                bg: 'background.actions.tertiary-active'
            },
            _disabled: {
                pointerEvents: 'none',
                opacity: '0.5'
            },
            _last: {
                borderBottomWidth: '0'
            },
            _icon: {
                width: 'spacing-16',
                height: 'spacing-16'
            }
        },
        control: {
            pos: 'relative'
        },
        itemText: {
            flex: '1'
        },
        itemGroup: {
            _first: {
                mt: '0'
            }
        },
        itemGroupLabel: {
            py: '1',
            fontWeight: 'medium'
        },
        label: {
            fontWeight: 'medium',
            userSelect: 'none',
            textStyle: 'body.xsmall',
            _disabled: {
                layerStyle: 'disabled'
            }
        },
        valueText: {
            lineClamp: '1',
            maxW: 'calc(100% - 1rem)',
            _disabled: {
                color: 'text.tertiary'
            }
        },
        clearTrigger: {
            color: 'text.tertiary',
            pointerEvents: 'auto',
            focusVisibleRing: 'inside',
            focusRingWidth: '0',
            rounded: 'xs'
        }
    },
    variants: {
        variant: {
            default: {
                trigger: {
                    backgroundColor: 'background.actions.tertiary',
                    _hover: {
                        borderColor: 'border.medium',
                        backgroundColor: 'background.actions.tertiary-hover'
                    },
                    _active: {
                        borderColor: 'border.strong',
                        backgroundColor: 'background.actions.tertiary-active'
                    },
                    _focusVisible: {
                        borderColor: 'border.strong',
                        backgroundColor: 'background.actions.tertiary-active'
                    },
                    _invalid: {
                        borderColor: 'border.system.error'
                    },
                    _selected: {backgroundColor: 'background.actions.tertiary-active'}
                },
                content: {
                    backgroundColor: 'background.actions.tertiary'
                },
                item: {
                    _highlighted: {
                        backgroundColor: 'background.actions.tertiary-active'
                    },
                    _selected: {backgroundColor: 'background.actions.tertiary-active'}
                }
            },
            filled: {
                trigger: {
                    backgroundColor: 'var(--module-bg-color)',

                    _hover: {
                        borderColor: 'border.medium'
                    },
                    _active: {
                        borderColor: 'border.strong'
                    },
                    _focusVisible: {
                        borderColor: 'border.strong'
                    },
                    _invalid: {
                        borderColor: 'border.system.error'
                    }
                },
                content: {
                    backgroundColor: 'background.section.default'
                },
                item: {
                    _highlighted: {
                        backgroundColor: 'background.section.strong'
                    },
                    _selected: {backgroundColor: 'background.section.strong'}
                }
            }
        },
        size: {
            md: {
                root: {
                    '--select-trigger-padding-x': 'spacing.spacing-16'
                },
                content: {
                    textStyle: 'body.medium',
                    borderBottomLeftRadius: 's',
                    borderBottomRightRadius: 's'
                },
                itemGroup: {
                    mt: 'spacing-6'
                },
                item: {
                    py: 'spacing-12',
                    px: 'spacing-12',
                    _selected: {textStyle: 'heading.medium'}
                },
                itemIndicator: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                },
                itemGroupLabel: {
                    py: 'spacing-12',
                    px: 'spacing-16'
                },
                trigger: {
                    py: 'spacing-16',
                    px: 'spacing-16',
                    textStyle: 'body.medium',
                    gap: 'spacing-8'
                },
                indicator: {
                    _icon: {
                        width: 'spacing-16',
                        height: 'spacing-16'
                    }
                }
            }
        }
    },
    defaultVariants: {
        size: 'md',
        variant: 'default'
    }
})
